import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../shared/hoc/createAdminPage'
import FacilityForReservation from '../../facilities/FacilityForReservation'
import {ColumnFlexContainer, LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Box, Paper, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import {PageFooterArea, PageFotterAreaLeft, PageFotterAreaRight} from '../../shared/hoc/withAdminPageTemplate'
import PPTextField from '../../shared/forms/PPTextField'
import {BackLink, StyledLink} from '../../../styledComponents/adminPage/Links'
import {
    AdminDefaultButton,
    AdminDefaultMediumButton,
    AdminPrimaryButton,
} from '../../../styledComponents/adminPage/Buttons'
import {ReservationsRepositoryForAdmin} from './ReservationsRepositoryForAdmin'
import {Nullable} from '../../../app/TypeHelpers'
import {AdminReserveLocationState, ReservationInfo} from './AdminReserveInterfaces'
import {AdminManagerReserveSteps, AdminReserveStepper} from './AdminReserveStepper'
import styled, {css} from 'styled-components'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {Messages} from '../../../Messages'
import {HKConstants} from '../../../HKConstants'
import lsHelper from './AdminReserveLocationStateHelper'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import {BoldSpan} from '../../../styledComponents/StyledSpan'
import {FacilityRentalType, ReservationClass} from '../ReservationInterfaces'
import AdminUserRepository, {
    ConditionForGetReservableUsersForManagerReservation,
} from '../../adminUser/AdminUserRepository'
import AdminUserSimple from '../../adminUser/AdminUserSimple'
import moment from 'moment'
import {formatDateLocal} from '../../../formatters/formatMoment'
import AdminReserveUsersRoomNumberInput from './AdminReserveUsersRoomNumberInput'
import {SelectedUserList} from '../../../__NoAutomatedTests/components/facilityReserve/repository/NatGetFacilityReserve'
import NatRidgeRepo from '../../../__NoAutomatedTests/components/ridge/repository/NatRidgeRepo'
import NatRoomToResidentRepo
    from '../../../__NoAutomatedTests/components/roomToResident/repository/NatRoomToResidentRepo'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    ridgeRepo: NatRidgeRepo
    roomToResidentRepo: NatRoomToResidentRepo
    reservationsRepoForAdmin: ReservationsRepositoryForAdmin
    adminUserRepo: AdminUserRepository
    location: Location<AdminReserveLocationState>
}

enum SearchStatus {
    BEFORE_SEARCH,
    SUCCESS,
    NO_RESULT,
    LIMIT_OVER,
}

interface State {
    facilityData: Nullable<FacilityForReservation>
    familyName: string
    firstName: string
    familyNameKana: string
    firstNameKana: string
    userSearchStatus: SearchStatus
    userInfos: Nullable<AdminUserSimple[]>
    selectedUser: Nullable<AdminUserSimple>
    selectedRoomsOrDisabled: Nullable<SelectedUserList[]>
    isDataSelectCompleted: boolean
}

const KEY_PREVIOUS_PAGE_URL: string = 'AdminManagerReserveSelectUserPage.previousPageUrl'
const KEY_PREVIOUS_PAGE_NAME: string = 'AdminManagerReserveSelectUserPage.previousPageName'

class AdminManagerReserveSelectUserPage extends Component<Props & AdminPagePropsSet, State> {
    private locationState: AdminReserveLocationState
    private previousPageUrl: string
    private previousPageName: string

    private readonly residentListHeadLabels: string[] =
        ['', '氏名']

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        if (!this.props.location || !this.props.location.state || !this.props.location.state.reservationInfo) {
            throw Error('前画面からの引き継ぎ情報がありません。画面遷移が不正です。')
        }
        const {previousPageUrl, previousPageName, ...rest} = this.props.location.state
        this.locationState = lsHelper.fromObject(rest)

        if (previousPageUrl !== undefined && previousPageName !== undefined) {
            this.previousPageUrl = previousPageUrl
            this.previousPageName = previousPageName
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_URL, previousPageUrl)
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_NAME, previousPageName)
        } else {
            const url: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_URL)
            const name: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_NAME)
            if (!url || !name) {
                throw Error('前画面のリンク情報がありません。画面遷移が不正です。')
            }
            this.previousPageUrl = url || ''
            this.previousPageName = name || ''
        }

        this.state = {
            facilityData: null,
            familyName: '',
            firstName: '',
            familyNameKana: '',
            firstNameKana: '',
            userSearchStatus: SearchStatus.BEFORE_SEARCH,
            userInfos: null,
            selectedUser: null,
            selectedRoomsOrDisabled: null,
            isDataSelectCompleted: false,
        }

        this.handleChangeUserList = this.handleChangeUserList.bind(this)
        this.handleChangeFamilyName = this.handleChangeFamilyName.bind(this)
        this.handleChangeFirstName = this.handleChangeFirstName.bind(this)
        this.handleChangeFamilyNameKana = this.handleChangeFamilyNameKana.bind(this)
        this.handleChangeFirstNameKana = this.handleChangeFirstNameKana.bind(this)
        this.handleClickClearSearchCondition = this.handleClickClearSearchCondition.bind(this)
        this.handleClickSearch = this.handleClickSearch.bind(this)
        this.handleClickSelectUser = this.handleClickSelectUser.bind(this)
        this.handleClickPreviousPage = this.handleClickPreviousPage.bind(this)
        this.handleClickSubmitWithOption = this.handleClickSubmitWithOption.bind(this)
        this.handleClickSubmitWithoutOption = this.handleClickSubmitWithoutOption.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const facilityData: Nullable<FacilityForReservation> = await this.props.asyncCallFunction(
                this.props.reservationsRepoForAdmin.getFacilityForReservation.bind(
                    this.props.reservationsRepoForAdmin,
                    this.props.condominiumCd,
                    this.props.location.state.facilityId,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (facilityData === null) {
                throw Error('facilityData cannot be null')
            }

            if (this.locationState.reservationInfo.classification === ReservationClass.AGENT) {
                throw Error('管理者予約の画面に代行予約の情報が渡されています。')
            }

            const {userInfo, selectedRoomsOrDisabled} = this.locationState.reservationInfo
            if (userInfo !== null) {
                let newSelectedRoomsOrDisabled: Nullable<SelectedUserList[]>
                if (!facilityData.accessControlManager) {
                    newSelectedRoomsOrDisabled = null
                } else if (selectedRoomsOrDisabled === null) {
                    newSelectedRoomsOrDisabled = []
                } else {
                    newSelectedRoomsOrDisabled = selectedRoomsOrDisabled
                }
                this.restoreFromLocationState(facilityData, userInfo, newSelectedRoomsOrDisabled)
            } else {
                const newSelectedRoomsOrDisabled: Nullable<SelectedUserList[]> =
                    facilityData.accessControlManager ? [] : null
                this.setState({facilityData, selectedRoomsOrDisabled: newSelectedRoomsOrDisabled})
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    restoreFromLocationState(
        facilityData: FacilityForReservation,
        userInfo: AdminUserSimple,
        selectedRoomsOrDisabled: Nullable<SelectedUserList[]>,
    ): void {
        const isDataSelectCompleted = this.checkDataSetCompleted(userInfo)

        this.setState({
            facilityData: facilityData,
            userSearchStatus: SearchStatus.SUCCESS,
            userInfos: [userInfo],
            selectedUser: userInfo,
            selectedRoomsOrDisabled: selectedRoomsOrDisabled,
            isDataSelectCompleted: isDataSelectCompleted,
        })
    }

    handleChangeFamilyName(event: React.ChangeEvent<HTMLInputElement>): void {
        const familyName = event.target.value
        this.setState({familyName})
    }

    handleChangeFirstName(event: React.ChangeEvent<HTMLInputElement>): void {
        const firstName = event.target.value
        this.setState({firstName})
    }

    handleChangeFamilyNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const familyNameKana = event.target.value
        this.setState({familyNameKana})
    }

    handleChangeFirstNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const firstNameKana = event.target.value
        this.setState({firstNameKana})
    }

    handleClickClearSearchCondition(): void {
        this.setState({
            familyName: '',
            firstName: '',
            familyNameKana: '',
            firstNameKana: '',
        })
    }

    async handleClickSearch(): Promise<void> {
        const {facilityData} = this.state
        if (facilityData === null) {
            throw Error('facilityData cannot be null')
        }

        await this.props.eventEmitter.showLoading()
        try {
            const searchCondition: ConditionForGetReservableUsersForManagerReservation = this.createSearchCondition()
            const response: Nullable<AdminUserSimple[]> =
                await this.props.asyncCallFunction(
                    this.props.adminUserRepo.getReservableUsersForManagerReservation.bind(
                        this.props.adminUserRepo,
                        searchCondition,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
            if (response === null) {
                return
            }

            let userInfos: Nullable<AdminUserSimple[]> = null
            let userSearchStatus: SearchStatus
            let selectedUser: Nullable<AdminUserSimple> = null

            if (response.length === 0) {
                userSearchStatus = SearchStatus.NO_RESULT
            } else if (response.length > 99) {
                userSearchStatus = SearchStatus.LIMIT_OVER
            } else {
                userSearchStatus = SearchStatus.SUCCESS
                userInfos = response

                if (userInfos.length === 1) {
                    selectedUser = userInfos[0]
                }
            }

            const selectedRoomsOrDisabled: Nullable<SelectedUserList[]> = facilityData.accessControlManager ? [] : null
            const isDataSelectCompleted = this.checkDataSetCompleted(selectedUser)

            this.setState({
                userSearchStatus,
                userInfos,
                selectedUser,
                selectedRoomsOrDisabled,
                isDataSelectCompleted,
            })

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    createSearchCondition(): ConditionForGetReservableUsersForManagerReservation {
        const {reservationInfo} = this.locationState
        const startDate: string = reservationInfo.startDate
        let endDate: string
        if (reservationInfo.type === FacilityRentalType.OVERNIGHT) {
            endDate = formatDateLocal(moment(startDate).add(reservationInfo.stayCount, 'day'))
        } else {
            endDate = reservationInfo.endDate
        }

        const {familyName, firstName, familyNameKana, firstNameKana} = this.state
        return {
            condominiumCd: this.props.condominiumCd,
            familyName: familyName === '' ? undefined : familyName,
            firstName: firstName === '' ? undefined : firstName,
            familyNameKana: familyNameKana === '' ? undefined : familyNameKana,
            firstNameKana: firstNameKana === '' ? undefined : firstNameKana,
            withinTerm: {startDate, endDate},
        }
    }

    checkDataSetCompleted(selectedUser: Nullable<AdminUserSimple>): boolean {
        return selectedUser !== null
    }

    handleClickSelectUser(selectedUser: AdminUserSimple): () => void {
        const isDataSelectCompleted = this.checkDataSetCompleted(selectedUser)

        return () => {
            this.setState({selectedUser, isDataSelectCompleted})
        }
    }

    handleClickPreviousPage(): void {
        this.props.navigate(
            this.previousPageUrl,
            {
                replace: true,
                state: lsHelper.toObject(this.locationState),
            },
        )
    }

    handleClickSubmitWithOption(): void {
        this.handleClickSubmit(`/admin/condominiums/${this.props.condominiumCd}/manager-reserve/select-option`)
    }

    handleClickSubmitWithoutOption(): void {
        this.handleClickSubmit(`/admin/condominiums/${this.props.condominiumCd}/manager-reserve/confirm`)
    }

    private handleClickSubmit(nextPath: string): void {
        if (this.locationState.reservationInfo.classification === ReservationClass.AGENT) {
            throw Error('管理者予約の代行画面に予約の情報が渡されています。')
        }

        const {
            selectedUser,
            selectedRoomsOrDisabled,
            facilityData,
        } = this.state

        if (selectedUser === null) {
            return
        }

        const {reservationInfo, ...rest} = this.locationState
        const newReservationInfo: ReservationInfo = {
            ...reservationInfo,
            userInfo: selectedUser,
            selectedRoomsOrDisabled: selectedRoomsOrDisabled,
        }
        const newAdminReserveLocationState: AdminReserveLocationState = {
            ...rest,
            reservationInfo: newReservationInfo,
            facilityData: facilityData,
            previousPageUrl:
                `/admin/condominiums/${this.props.condominiumCd}/manager-reserve/select-user`,
            previousPageName: '利用者選択',
        }

        this.props.navigate(
            nextPath,
            {
                replace: true,
                state: lsHelper.toObject(newAdminReserveLocationState),
            },
        )
    }

    renderSearchFormArea(): ReactNode {
        return (<Paper>
            <ListSearchFormArea onEnterKey={this.handleClickSearch}>
                <ConditionTable>
                    <ConditionTableBody>
                        {this.renderResidentConditionAndButtonRow()}
                    </ConditionTableBody>
                </ConditionTable>
            </ListSearchFormArea>
        </Paper>)
    }

    renderResidentConditionAndButtonRow(): ReactNode {
        return (<>
            <ConditionTableRow>
                <ConditionCategoryCell rowSpan={2}>管理者情報</ConditionCategoryCell>
                <ConditionColumNameCell>氏名</ConditionColumNameCell>
                <ConditionInputCell>
                    <LeftRowFlexContainer>
                        <FlexContainerColumn>
                            <span>姓</span>
                            <NameWrapper>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 10,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.handleChangeFamilyName}
                                    value={this.state.familyName}
                                />
                            </NameWrapper>
                        </FlexContainerColumn>
                        <FlexContainerColumn>
                            <span>名</span>
                            <NameWrapper>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 10,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.handleChangeFirstName}
                                    value={this.state.firstName}
                                />
                            </NameWrapper>
                        </FlexContainerColumn>
                    </LeftRowFlexContainer>
                </ConditionInputCell>
            </ConditionTableRow>
            <ConditionTableRow>
                <ConditionColumNameCell>氏名(フリガナ)</ConditionColumNameCell>
                <ConditionInputCell>
                    <LeftRowFlexContainer>
                        <FlexContainerColumn>
                            <span>セイ</span>
                            <NameWrapper>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 20,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.handleChangeFamilyNameKana}
                                    value={this.state.familyNameKana}
                                />
                            </NameWrapper>
                        </FlexContainerColumn>
                        <FlexContainerColumn>
                            <span>メイ</span>
                            <NameWrapper>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 20,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.handleChangeFirstNameKana}
                                    value={this.state.firstNameKana}
                                />
                            </NameWrapper>
                        </FlexContainerColumn>
                    </LeftRowFlexContainer>
                </ConditionInputCell>
                <ConditionButtonCell>
                    <ColumnFlexContainer>
                        <AdminDefaultMediumButton onClick={this.handleClickSearch}>
                            検索する
                        </AdminDefaultMediumButton>
                        <Box mt={'10px'}>
                            <StyledLink onClick={this.handleClickClearSearchCondition}>検索条件をクリア</StyledLink>
                        </Box>
                    </ColumnFlexContainer>
                </ConditionButtonCell>
            </ConditionTableRow>
        </>)
    }

    renderResidentUserListArea(): ReactNode {
        let searchMessageArea: ReactNode = null
        let searchResultArea: ReactNode = null

        switch (this.state.userSearchStatus) {
            case SearchStatus.BEFORE_SEARCH:
                searchMessageArea = <div>{Messages.IOPCC0017.toString('検索する')}</div>
                break
            case SearchStatus.NO_RESULT:
                searchMessageArea = <div>{Messages.IOPCC0014.toString()}</div>
                break
            case SearchStatus.LIMIT_OVER:
                searchMessageArea =
                    <div>{Messages.IOPCC0016.toString(HKConstants.VMN.RES.RESIDENCE_USER_SEARCH_RESULTS_LIMIT)}</div>
                break
            case SearchStatus.SUCCESS:
                const {userInfos} = this.state
                if (userInfos === null || userInfos.length === 0) {
                    throw Error('検索結果ステータスが成功状態で検索結果が空です。')
                }

                searchMessageArea = <div>{`${userInfos.length}件該当しました。予約する管理者を選択してください。`}</div>
                searchResultArea = this.renderManagerList(userInfos)
                break
            default:
        }

        return (<>
            <Box mt={'10px'} mb={'10px'}>
                {searchMessageArea}
            </Box>
            <Paper>
                <Box flexGrow={10}>
                    {searchResultArea}
                </Box>
            </Paper>
        </>)
    }

    renderManagerList(userInfos: AdminUserSimple[]): ReactNode {
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        {
                            this.residentListHeadLabels
                                .map((label, index) => <TableCell key={index}>{label}</TableCell>)
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {userInfos.map((info, index) => this.renderResidentListRow(info, index))}
                </TableBody>
            </Table>
        </>
    }

    renderResidentListRow(userInfo: AdminUserSimple, index: number): ReactNode {
        const displayUserName = `${userInfo.familyName} ${userInfo.firstName}`

        return (<SelectableTableRow key={index} selected={this.state.selectedUser === userInfo}>
            <SelectButtonCell>
                <ToggleButton
                    size={'small'}
                    selected={this.state.selectedUser === userInfo}
                    onClick={
                        this.handleClickSelectUser(
                            userInfo,
                        )
                    }>
                    {this.state.selectedUser === userInfo ? '選択済み' : '選択する'}
                </ToggleButton>
            </SelectButtonCell>
            <TableCell>{displayUserName}</TableCell>
        </SelectableTableRow>)
    }

    renderCardKeysArea(): ReactNode {
        const {selectedUser, selectedRoomsOrDisabled, facilityData} = this.state
        if (selectedUser === null ||
            selectedRoomsOrDisabled === null ||
            facilityData === null ||
            facilityData.accessControlManager === null
        ) {
            return null
        }

        return (<>
            <PaddingPaper>
                <AdminReserveUsersRoomNumberInput
                    condominiumCd={this.props.condominiumCd}
                    roomToResidentRepo={this.props.roomToResidentRepo}
                    ridgeRepo={this.props.ridgeRepo}
                    onChangeUserList={this.handleChangeUserList}
                    eventEmitter={this.props.eventEmitter}
                    selectedUserList={selectedRoomsOrDisabled}
                    excludeSelectedUser={null}
                    asyncCallFunction={this.props.asyncCallFunction}
                    navigate={this.props.navigate}
                    location={this.props.location}
                    validPeriodCriteriaDate={moment(this.locationState.reservationInfo.startDate)}
                    accessControlManager={facilityData.accessControlManager}
                />
            </PaddingPaper>
        </>)
    }

    handleChangeUserList(users: SelectedUserList[]): void {
        this.setState({
            selectedRoomsOrDisabled: users,
        })
    }

    render(): ReactNode {
        const {facilityData, selectedUser} = this.state
        if (!facilityData) {
            return null
        }

        const hasOptions: boolean = facilityData.facilityOptions.length > 0

        return (<>
            <Box height={'108px'} bgcolor={'#ffffff'} padding={'24px'}>
            <AdminReserveStepper
                classification={ReservationClass.MANAGER}
                step={AdminManagerReserveSteps.SELECT_USER}
                hasOption={hasOptions}
            />
            </Box>
            <Box mt={'10px'} mb={'10px'}>
                <BoldSpan>{facilityData.facilityName}</BoldSpan> を管理者予約受付
                <div>{Messages.IFACA0004.toString()}</div>
            </Box>
            {this.renderSearchFormArea()}
            {this.renderResidentUserListArea()}
            {
                selectedUser !== null &&
                <Box mt={'10px'} mb={'10px'}>
                    <BoldSpan>{`${selectedUser.familyName} ${selectedUser.firstName}`}</BoldSpan> さんを選択しています。
                </Box>
            }
            {this.renderCardKeysArea()}
            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink onClick={this.handleClickPreviousPage}>
                        {`${this.previousPageName}に戻る`}
                    </BackLink>
                </PageFotterAreaLeft>
                <PageFotterAreaRight>
                    {
                        facilityData.facilityOptions.length > 0
                            ? <AdminPrimaryButton
                                onClick={this.handleClickSubmitWithOption}
                                disabled={!this.state.isDataSelectCompleted}
                            >
                                オプション選択に進む
                            </AdminPrimaryButton>
                            : <AdminPrimaryButton
                                onClick={this.handleClickSubmitWithoutOption}
                                disabled={!this.state.isDataSelectCompleted}
                            >
                                予約内容確認に進む
                            </AdminPrimaryButton>
                    }
                </PageFotterAreaRight>
            </PageFooterArea>
        </>)
    }
}

export default createAdminPage(AdminManagerReserveSelectUserPage)

interface ToggleButtonProps {
    selected: boolean
}

const ToggleButton = styled(AdminDefaultButton)<ToggleButtonProps>`
  ${props => {
    if (props.selected) {
      return css`
        &.MuiButton-contained, &.MuiButton-contained:hover {
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08), 0 4px 10px -6px #000000 inset;
          -moz-box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08), 0 4px 10px -6px #000000 inset;
          -webkit-box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08), 0 4px 10px -6px #000000 inset;
        }
      `
    }
  }
  }`

const FlexContainerColumn: StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  &:first-child {
    padding: 0 12px 0 0;
  }
`

const NameWrapper: StyledDiv = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;
`

const PaddingPaper = styled(Paper)`
  padding: 16px;
`

const SelectButtonCell = styled(TableCell)`
  &.MuiTableCell-root {
    white-space: nowrap;
    width: 1px;
  }
`

const SelectableTableRow = styled(TableRow)`
  background-color: ${props => props.selected ? 'rgb(255, 217, 102)!important' : 'white'};
`
